import React, { useMemo, useState } from 'react';
import { Card, Text, Icon, Bubble } from "tea-component";
import { useIntl, getLocale } from 'umi';
import cx from 'classnames';
import * as echarts from 'echarts';
import { formatNum, formatPercentNum, formatSmallValue, getIsErrorValue, jumpDetail, unitConversion } from '@/pages/dashboard/utils/utils';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import SvgIcon from '@/pages/home/components/SvgIcon';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { getCardChart, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import useOverviewLocalesIntl from '@/pages/dashboard/pages/overview/_config/useOverviewLocalesIntl';
import ACard from '@/pages/dashboard/components/ACard';
import KeyPerformanceBenchmarkPopover from '@/pages/dashboard/pages/overview/components/KeyPerformanceMetrics/KeyPerformanceBenchmarkPopover';
import { isKeyPerformanceBenchmarkCardConfigured } from '@/pages/dashboard/pages/overview/_config/keyPerformanceBenchmark';
import "@/pages/dashboard/assets/scss/page/Overlay/components/cartContext.scss";
/**
* 卡片主数据
*/
export interface MetricsItem {
  metricsName: string; // 卡片名称
  value: string; // 卡片主数据值
  dod: string; // 同比(时间范围=1天)
  sdlw: string; // 环比(时间范围=1天)
  type: string;
  title: string;
  time: string;
  dodTime: string;
  sdlwTime: string;
  dodCount: string;
  sdlwCount: string;
  cardHourTime: string;
}
interface MetricsData {
  show_value: string;  // 变化率
  this_time: string;   // 本期数据时间(时间范围>1天)
  this_value: string;  // 本期数据值(时间范围>1)
  last_time: string;   // 上期数据时间(时间范围>1天)
  last_value: string;  // 上期数据值(时间范围>1天)
}
/**
 * 卡片主数据
 */
export interface MetricsItemInfo {
  name: string;    // 卡片名称
  value: string;           // 卡片主数据值
  dod?: MetricsData;        // 同比(时间范围=1天)
  sdlw?: MetricsData;       // 环比(时间范围=1天)
  wow?: MetricsData;        // 同比(时间范围=1周)
  mom?: MetricsData;        // 同比(时间范围=1周)
  swlm?: MetricsData;       // 环比(时间范围=1月)
  smly?: MetricsData;       // 环比(时间范围=1月)

  change: MetricsData;     // 变化率(时间范围>1天)
  update_time?: string;    // 数据更新时间(overview无)
}

export default function CardContext(props: any) {
  const {
    dataSource,
    index,
    formValue,
    isChange,
    userQuota,
    dateDimension,
    enableKeyPerformanceBenchmark = false,
    gameCode,
  } = props;
  const { formatMessage } = useIntl();
  const { getLocalText } = useLocal();
  const [isClick, setIsClick] = useState(false);
  const {
    name,
    metrics_name,
    value,
    update_time,
    jumpUrl,
    explain,
    trendData,
    xAxisData,
    ...otherData
  } = dataSource || {};

  const showKeyPerformanceBenchmark =
    enableKeyPerformanceBenchmark &&
    isKeyPerformanceBenchmarkCardConfigured(gameCode, metrics_name);

  const seriesNameFirst = dateDimension === 'DAY' ? 'today' : dateDimension === 'WEEK' ? 'week' : 'month';
  const seriesNameTwo = dateDimension === 'DAY' ? 'sdlw' : dateDimension === 'WEEK' ? 'swlm' : 'smly';

  const series = useMemo(() => trendData?.map((t: string[], i: number) => ({
    name: i === 0 ? getLocalText(seriesNameFirst) : getLocalText(seriesNameTwo),
    type: 'line',
    showSymbol: t?.length === 1 || t?.filter((e) => e)?.length === 1, // 只有一个点的时候需要显示圆点
    symbol: 'circle',
    z: i === 0 ? 2 : 1,
    lineStyle: {
      type: i === 0 ? 'solid' : 'dotted'
    },
    areaStyle: {
      opacity: 0.25,
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: i === 0 ? '#5086F3' : '#69D19C'
        },
        {
          offset: 1,
          color: '#FFF'
        },
      ])
    },
    emphasis: {
      focus: 'series'
    },
    data: t,
  })), [trendData, getLocale()]);

  const ICONS = ['user-u', 'money', 'user-plus', 'user-success', 'user-dollar', 'pie-chart-icon'];

  const { METRICS_INFO } = useOverviewLocalesIntl();

  const jumptoDetail = () => {
    setIsClick(true);
    jumpDetail(`/dashboard/game${jumpUrl}`, formValue);
  };

  // 卡片下方显示同比和环比涨跌的组件
  const UpsAndDownsComparison = () => {
    const keys = Object.keys(otherData);
    return (
      <div className="overlay-card-body-flex">
        <div className='overlay-card-body-flex-item'>
          {
            keys.map((key, ki) => (
              (isChange && key === 'change') || (!isChange && key !== 'change') ?
                <div key={ki} className='overlay-card-body-text'>{formatMessage({ id: `overview_${key}` })}</div> : null
            ))
          }
        </div>
        <div className='overlay-card-body-flex-rightitem'>
          {
            keys.map((key, ki) => {
              const { show_value } = otherData[key];
              const correctVal = !getIsErrorValue(show_value);
              const val = !(Number(show_value) < 0);
              return (
                (isChange && key === 'change') || (!isChange && key !== 'change') ?
                  <div key={ki} style={{ justifyContent: 'flex-start' }} className={cx({ 'overlay-card-body-textnum': true }, { ["text-green"]: correctVal && val }, { ["text-red"]: correctVal && !val })}>
                    {correctVal ? formatPercentNum(parseFloat(show_value), 1, true) : <span className="data-null"> -</span>}
                    {
                      correctVal && ((Number(show_value) === 0)
                        ? <Icon type={'trend-zero'} />
                        : (Number(show_value) > 0)
                          ? <SvgIcon className='overlay-card-body-icon' type="green-up-new" />
                          : <SvgIcon className='overlay-card-body-icon' type="red-down-new" />)
                    }

                  </div> : null
              );
            })
          }
        </div>
      </div>
    );
  };

  // 浮窗提示
  const bubbleContentList = (() => {
    const keys = Object.keys(otherData);
    return keys.map((key) => ((isChange && key === 'change') || (!isChange && key !== 'change')) ? {
      title: formatMessage({ id: `overview_${key}` }),
      ...otherData[key]
    } : undefined);
  })();

  /**
   * @param key metrics_name标识 根据标识展示unit
   * @param value 数值
   */
  const formatterValue = (key: string, value: string): string => {
    if (getIsErrorValue(value)) return '-';
    const _foramtVal: string = formatNum(value, false, userQuota.includes(name));
    return METRICS_INFO[key] ? METRICS_INFO[key].font + _foramtVal + METRICS_INFO[key].end
      : _foramtVal;
  };
  const getVal = (val: string, key?: undefined) => (
    !getIsErrorValue(val) &&
      METRICS_INFO[metrics_name]?.type === 'percent' ? formatPercentNum(parseFloat(val), 2) : formatterValue(key || '-', val));

  /**
   * @param data 卡片变化率数据
   */
  const itemCont = (data: any, ind: number) => {
    if (!data) return;
    const { title, this_time, last_time, this_value, last_value, explain } = data || {};
    const isEn = getLocale() === 'en-US';
    const unit = METRICS_INFO[metrics_name]?.unit || '';
    const topVal = getVal(this_value);
    const bottomVal = getVal(last_value);
    return (
      <div className='bubble-item' key={`${ind}itemCont`}>
        <div className='bubble-title'>{title} <Text className='bubble-desc'>{isEn && explain ? `(${explain})` : ''}</Text></div>
        <div className='bubble-flex'>
          <div className='bubble-text'>
            <div className='bubble-t'>{this_time}</div>
            <div className='bubble-b'>{last_time}</div>
          </div>
          <div className='bubble-ct'>VS</div>
          <div className='bubble-text'>
            <div className='bubble-t'>{(topVal !== '-' ? unit : '') + topVal}</div>
            <div className='bubble-b'>{(bottomVal !== '-' ? unit : '') + bottomVal}</div>
          </div>
        </div>
      </div>
    );
  };

  const insertStr = (source: string, start: number, symbolStr: string) => source.slice(0, start) + symbolStr + source.slice(start);

  const formatterFunc = (param: string | any[], title: any, _isPercent = false, _isFloating = false,) => {
    const date = dateDimension === 'MONTH' ? insertStr(insertStr(param[0]?.axisValue, 4, '-'), 7, '') : insertStr(insertStr(param[0]?.axisValue, 4, '-'), 7, '-');
    const rightTitleName = name?.split('(T-1)')?.[0];
    let returnStr = `<div class=${'basic-formatter-box'}>
      <div class=${'basic-formatter-header'}>
      <p class='basic-formatter-one basic-formatter-title'>${date}</p>
      <p class='basic-formatter-two'>${rightTitleName}</p></div>`;
    for (let i = 0;i < param.length;i++) {
      const paramValue = param[i].value;
      const dauValue = [0, 1, 2].includes(index) ? formatNum(paramValue, false, userQuota.includes(dataSource?.name)) : index === 4 ? formatSmallValue(paramValue, 'decimals', 2) : formatPercentNum(paramValue, 2, false);
      const name = param[i].seriesName;
      const radio = `<span style="display:inline-block;margin-right:4px;border-radius:8px;width:8px;height:8px;background-color:${param?.[i]?.color}"></span>`;
      returnStr += `<div class=${'basic-formatter-header'}>
        <p class=${'basic-formatter-one'}>${radio}&nbsp;${name}</p>
        <p class=${'basic-formatter-two'}>${dauValue}</p></div>`;
    }
    returnStr = `${returnStr}`;
    return returnStr;
  };

  return (
    <ACard url={jumpUrl ? `/dashboard/game${jumpUrl}` : ''}>
      <div onClick={jumptoDetail}>
        <Card >
          <Card.Body
            className={isClick ? 'overlay-card overlay-card-click' : 'overlay-card'}
            title={
              <div
                className={cx(
                  'dbd-overlay-card-title-wrap',
                  showKeyPerformanceBenchmark &&
                    'dbd-overlay-card-title-wrap--has-benchmark',
                )}
              >
                <div className='overlay-card-title'>
                  <SvgIcon className='overlay-card-icon' type={ICONS[index]} />
                  <Bubble
                    dark
                    placement="top"
                    content={<div className='overlay-card-desc' dangerouslySetInnerHTML={{ __html: explain }}></div>}
                  >
                    <div className='overlay-card-text'>{name}</div>
                  </Bubble>
                  <SvgIcon className='overlay-card-righticon' type='arrow-right' />
                </div>
                {showKeyPerformanceBenchmark ? (
                  <div
                    className="dbd-overlay-card-benchmark-slot"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <KeyPerformanceBenchmarkPopover metricsName={metrics_name} />
                  </div>
                ) : null}
              </div>
            }
          >
            <div className='overlay-card-body'>
              <div className='overlay-card-body-left'>
                <div className='overlay-card-body-num'>{value}</div>
                <Bubble
                  trigger="hover"
                  dark
                  placement="bottom-start"
                  content={
                    bubbleContentList.map((i, ind: number) => itemCont(i, ind))
                  }
                >
                  {UpsAndDownsComparison()}
                </Bubble>
              </div>
              <div className='overlay-card-body-right'>
                <LineChart
                  height={75}
                  xAxisData={xAxisData || []}
                  series={series || []}
                  isShowLegend={false}
                  formatterFunc={formatterFunc}
                  setOtherConfigFunc={(opt: any) => {
                    opt = getCardChart(opt);
                    opt = xAxisHighLight(opt, {
                      lineStyle: {
                        type: 'solid'
                      },
                      formatter: (params: any) => params?.value ? params?.value?.slice(-2) : '',
                      symbolSize: 8,
                      fontSize: 11,
                    });
                    opt.grid = {
                      x: 10,
                      y: 12,
                      x2: 2,
                      y2: 0,
                      containLabel: true
                    };
                    if (index === 3 || index === 5) {
                      opt.yAxis.axisLabel.formatter = (value: number) => yAxisFormat(value, true, 2, true);
                    }

                    if (index === 4) {
                      const dataList: any = [];
                      series?.map((item: any) => dataList.push(...item?.data));
                      opt.yAxis.axisLabel.formatter = (value: number) => {
                        // 针对arpu特殊处理，因为值很小
                        const formatLabel = value ? unitConversion(value, true) : value;
                        const num = value < 1 && value > 0 ? formatSmallValue(value, 'decimals', 2) : formatLabel;
                        return num;
                      };
                    }
                    // 设计稿大小是11
                    opt.xAxis[0].axisLabel.fontSize = 11;
                    opt.xAxis[0].axisLabel.color = '#BBBDCB';
                    opt.yAxis.axisLabel.fontSize = 11;
                    opt.yAxis.axisLabel.color = '#BBBDCB';
                    opt.tooltip.confine = false; // 浮窗不限制在图表区域
                    opt.tooltip.axisPointer.label.padding = [5, 1, 0, 1];
                    return opt;
                  }}
                />
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </ACard>
  );
};
